/**
 * Flutter ↔ WebView 통신 브릿지
 *
 * 규격: `aura-common/guide/FLUTTER_WEBVIEW_SPEC.md`
 *
 * 이 모듈은 **앱 안에서 열렸을 때만** 의미가 있다. 일반 브라우저에서는
 * `window.FlutterChannel` 이 없으므로 보내는 쪽은 조용히 아무것도 하지 않고,
 * 받는 쪽은 이벤트가 오지 않을 뿐이다. 그래서 분기 없이 항상 초기화해도 된다.
 */

import { goBackInApp } from '@/utils/app-back'

/* ================================================================
 * 기본 송수신
 * ================================================================ */

/** 앱(WebView) 안에서 열렸는가. */
export function isFlutterApp() {
  return typeof window !== 'undefined' && !!window.FlutterChannel
}

/**
 * Vue → Flutter 메시지 전송.
 *
 * 브라우저에서는 채널이 없어 아무 일도 일어나지 않는다. 호출하는 쪽이
 * 매번 `isFlutterApp()` 을 묻지 않아도 되게 여기서 삼킨다.
 */
export function sendToFlutter(type, payload = {}) {
  if (!isFlutterApp()) return false

  window.FlutterChannel.postMessage(JSON.stringify({
    type,
    payload,
    timestamp: Date.now()
  }))
  return true
}

/**
 * Flutter → Vue 메시지 수신 리스너 등록.
 *
 * @returns 해제 함수
 */
export function onFlutterMessage(type, callback) {
  const handler = (e) => {
    if (e.detail && e.detail.type === type) {
      callback(e.detail.payload || {}, e.detail)
    }
  }
  window.addEventListener('flutter-message', handler)
  return () => window.removeEventListener('flutter-message', handler)
}

/* ================================================================
 * safe area
 * ================================================================ */

/**
 * Flutter 가 보낸 safe area 값을 CSS 변수에 넣는다.
 *
 * `main.css` 가 같은 변수를 `env(safe-area-inset-*)` 로 선언해 두었고, 여기서
 * `documentElement` 인라인 스타일로 덮는다(인라인이 `:root` 규칙을 이긴다).
 *
 * 즉 **2단 구조**다 — 브라우저·iOS 처럼 `env()` 가 제값을 주는 곳에서는 그대로
 * 쓰고, 앱 WebView 처럼 0 으로 나올 수 있는 곳은 Flutter 가 실측값으로 덮는다.
 * 그래서 화면 쪽은 `env()` 를 직접 쓰지 말고 항상 `var(--safe-area-*)` 를 써야
 * 한다. `env()` 를 직접 쓰면 Flutter 가 보낸 값이 그 화면만 비껴간다.
 */
export function initSafeArea() {
  return onFlutterMessage('SAFE_AREA', (payload) => {
    if (typeof payload.top === 'number') {
      document.documentElement.style.setProperty('--safe-area-top', `${payload.top}px`)
    }
    if (typeof payload.bottom === 'number') {
      document.documentElement.style.setProperty('--safe-area-bottom', `${payload.bottom}px`)
    }
  })
}

/* ================================================================
 * 안드로이드 뒤로가기
 * ================================================================ */

/**
 * 안드로이드 물리 뒤로가기를 웹이 받아 처리한다.
 *
 * 규격의 3단계:
 *   1. Flutter 가 `BACK_BUTTON` 을 보낸다
 *   2. 뒤로 갈 곳이 있으면 `router.back()`
 *   3. 없으면 `BACK_PRESSED` 를 돌려주고, 앱이 종료 여부를 묻는다
 *
 * **반드시 답을 돌려준다.** Flutter 는 답이 없으면 스스로 판단하는데(짧은
 * 타임아웃), 그 경로를 타면 웹의 라우터 상태와 어긋날 수 있다. 여기서 끝까지
 * 답하는 쪽이 맞다.
 *
 * 뒤로 갈 곳이 있는지는 `window.history.state?.back` 으로 본다.
 * `history.length` 는 앞으로 간 기록까지 세어 믿을 수 없다 — 뒤로 간 뒤에도
 * 줄지 않아서, 첫 화면인데도 "뒤로 갈 수 있다"고 오판하게 된다.
 */
export function initBackButton(router) {
  return onFlutterMessage('BACK_BUTTON', () => {
    const modalClosed = closeTopmostOverlay()
    if (modalClosed) return

    // 앞 기록이 앱 화면이면 한 칸 뒤로, 앱 밖(본인인증 NICE 등)이면 홈으로 - utils/app-back(2026-11-02).
    // 이미 홈일 때만 BACK_PRESSED 로 답해 앱이 종료를 묻게 한다
    if (goBackInApp(router)) return
    sendToFlutter('BACK_PRESSED')
  })
}

/**
 * 열려 있는 오버레이가 있으면 뒤로가기로 그것부터 닫는다.
 *
 * 바텀시트가 떠 있는데 뒤로가기가 화면을 통째로 넘겨 버리면 사용자가 당황한다.
 * `data-back-dismiss` 가 붙은 요소를 약속으로 삼는다 — 해당 화면에서 그 속성만
 * 달아 두면 여기 코드를 고치지 않아도 걸린다.
 *
 * @returns 닫은 것이 있으면 true
 */
function closeTopmostOverlay() {
  const overlays = document.querySelectorAll('[data-back-dismiss]')
  if (overlays.length === 0) return false

  const top = overlays[overlays.length - 1]
  top.dispatchEvent(new CustomEvent('back-dismiss', { bubbles: false }))
  return true
}

/* ================================================================
 * 딥링크 / 푸시 이동
 * ================================================================ */

/**
 * 앱이 넘겨 준 경로로 이동한다.
 *
 * 알림을 탭했거나 밖에서 딥링크로 앱이 열렸을 때 온다. 앱이 죽어 있다가
 * 열린 경우에는 앱이 그 경로를 **첫 화면으로 바로 열기** 때문에 여기로 오지
 * 않는다. 여기 오는 것은 **앱이 이미 떠 있는 동안** 들어온 것뿐이다.
 *
 * 경로는 앱 밖에서 온 값이라 그대로 믿지 않는다. 앱 쪽에서도 한 번 거르지만
 * (`DeepLink.toWebPath`) 여기서도 같은 기준으로 본다 — 둘 중 하나가 뚫려도
 * 다른 하나가 막는다.
 */
export function initPushNavigate(router) {
  return onFlutterMessage('PUSH_NAVIGATE', (payload) => {
    const path = typeof payload.path === 'string' ? payload.path.trim() : ''
    // `//evil.com` 은 브라우저가 다른 호스트로 읽는다.
    if (!path.startsWith('/') || path.startsWith('//')) {
      return
    }
    const query = payload.query && typeof payload.query === 'object' ? payload.query : {}
    router.push({ path, query }).catch(() => {
      // 같은 경로로 다시 밀면 라우터가 거부한다. 알림을 두 번 탭한 경우라
      // 사용자가 보기에는 아무 문제가 없다.
    })
  })
}

/* ================================================================
 * 푸시
 * ================================================================ */

/**
 * 알림 권한을 묻는다.
 *
 * iOS 는 항상 필요하고 **Android 도 13(API 33)부터 런타임 권한**이다.
 * 앱이 결과를 `PUSH_PERMISSION_RESULT` 로 돌려준다.
 *
 * 결과의 `reason` 을 봐야 한다. `not_configured` 는 **사용자가 거부한 것이
 * 아니라 앱에 FCM 이 아직 안 붙은 것**이다. 그 둘을 뭉뚱그려 "설정에서 켜세요"
 * 라고 안내하면 설정에 가 봐야 켤 것이 없다.
 */
export function requestPushPermission() {
  return sendToFlutter('PUSH_PERMISSION_REQUEST')
}

/** 설정 화면에서 알림을 켜고 끈 것. */
export function setPushConsent(enabled) {
  return sendToFlutter('PUSH_CONSENT_CHANGED', { enabled: Boolean(enabled) })
}

/** 권한 팝업 결과. `(granted, reason)` */
export function onPushPermissionResult(callback) {
  return onFlutterMessage('PUSH_PERMISSION_RESULT', (payload) => {
    callback(payload.granted === true, payload.reason || '')
  })
}

/**
 * 앱이 떠 있을 때 알림이 온 것.
 *
 * 화면에 떠 있으면 트레이 알림 대신 인앱으로 알려야 한다. 알림함 화면이
 * 아직 없으므로(푸시 규격 9장 결정 ④) 여기서는 **window 이벤트로 흘려보내기만**
 * 한다. 화면이 생기면 그 화면이 받아 쓰면 되고, 지금 UI 를 지어 두면 나중에
 * 버릴 것이 된다.
 *
 * ```js
 * window.addEventListener('aura-push', (e) => console.log(e.detail))
 * ```
 */
export function initPushReceived() {
  return onFlutterMessage('PUSH_RECEIVED', (payload) => {
    window.dispatchEvent(new CustomEvent('aura-push', { detail: payload }))
  })
}

/* ================================================================
 * 외부 링크
 * ================================================================ */

/**
 * 외부 브라우저로 열어야 하는 링크를 앱에 넘긴다.
 *
 * WebView 안에서 `window.open` 은 대개 아무 일도 일어나지 않고, 외부 사이트를
 * WebView 안에 띄우면 사용자가 앱에서 빠져나갈 길이 없어진다.
 *
 * @returns 앱에 넘겼으면 true. 브라우저에서는 false 라 호출한 쪽이 평소대로 연다.
 */
export function openExternal(url) {
  return sendToFlutter('OPEN_EXTERNAL', { url })
}

/**
 * `target="_blank"` 링크를 앱이 외부 브라우저로 열게 한다.
 *
 * WebView 에는 새 창이 없어 `target="_blank"` 가 **아무 일도 하지 않는다.**
 * 사용자에게는 링크가 죽은 것으로 보인다.
 *
 * 링크마다 고치지 않고 클릭을 위임해서 받는다. 앞으로 생기는 링크도 그대로 걸린다.
 * 브라우저에서는 `openExternal` 이 false 를 주므로 기본 동작(새 탭)을 막지 않는다.
 */
export function initExternalLinks() {
  const onClick = (event) => {
    const anchor = event.target.closest && event.target.closest('a[target="_blank"]')
    if (!anchor) return

    const href = anchor.getAttribute('href') || ''
    if (!/^https?:\/\//i.test(href)) return

    if (openExternal(href)) {
      event.preventDefault()
    }
  }

  document.addEventListener('click', onClick)
  return () => document.removeEventListener('click', onClick)
}

/* ================================================================
 * 초기화
 * ================================================================ */

/**
 * 브릿지 전체를 켠다. `main.js` 에서 한 번 부른다.
 *
 * 라우터 이동을 앱에 알리는 것까지 여기서 묶는다. 앱이 현재 화면을 알아야
 * 푸시로 들어온 경로와 지금 화면을 비교할 수 있다.
 */
export function initFlutterBridge(router) {
  const teardown = [
    initSafeArea(),
    initBackButton(router),
    initExternalLinks(),
    initPushNavigate(router),
    initPushReceived(),
  ]

  router.afterEach((to) => {
    sendToFlutter('ROUTE_CHANGED', { path: to.path, name: to.name || '' })
  })

  // 앱이 "웹이 준비됐다" 를 알아야 safe area 를 보낼 시점을 잡을 수 있다.
  sendToFlutter('PAGE_LOADED', { path: router.currentRoute.value.path })

  return () => teardown.forEach((off) => off && off())
}
